import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FilePdf, PaperPlaneTilt, CheckCircle, Prohibit, Trash, Receipt } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogHeader, DialogBody, DialogFooter } from "@/components/ui/dialog";
import { AdminDialogHeading } from "@/components/admin/AdminDialogHeading";
import { cn } from "@/lib/utils";
import { throwIfNotOk } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import { formatDateWithOptionsBs } from "@/lib/format";
import { todayYmd } from "@/lib/company-expiry";
import { COMPANY_INVOICE_STATUS_CONFIG } from "@/lib/status-configs";
import type { CompanyInvoiceDetail } from "@/types";
import { dueLabel, fmtKm, invoiceDisplayStatus, invoicePeriodLabel } from "./invoice-format";

const lineDate = (iso: string) => formatDateWithOptionsBs(iso, { day: "numeric", month: "short", timeZone: "Europe/Sarajevo" });

export function InvoiceDetailDialog({
  invoiceId,
  companyId,
  onClose,
}: {
  invoiceId: string;
  companyId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [voiding, setVoiding] = useState(false);
  const [reason, setReason] = useState("");

  const { data: invoice } = useQuery({
    queryKey: ["admin", "company-invoice", invoiceId],
    queryFn: async () => {
      const res = await fetch(`/api/company-invoices/${invoiceId}`);
      await throwIfNotOk(res, "Greška pri učitavanju fakture");
      return res.json() as Promise<CompanyInvoiceDetail>;
    },
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["admin", "company-invoice", invoiceId] });
    queryClient.invalidateQueries({ queryKey: ["admin", "company", companyId, "invoices"] });
    queryClient.invalidateQueries({ queryKey: ["admin", "company", companyId, "payment-requests"] });
    queryClient.invalidateQueries({ queryKey: ["admin", "companies"] });
  };

  const action = useMutation({
    mutationFn: async (input: { path: string; body?: unknown; message: string }) => {
      const res = await fetch(`/api/company-invoices/${invoiceId}/${input.path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input.body ?? {}),
      });
      await throwIfNotOk(res, "Akcija nije uspjela");
      return res.json() as Promise<{ emailed?: boolean; result?: string }>;
    },
    onSuccess: (data, input) => {
      if (data.emailed === false) toast.error("Faktura je izdata, ali email nije poslan. Pošaljite je ponovo.");
      else toast.success(input.message);
      refresh();
      if (data.result === "deleted") onClose();
      setVoiding(false);
    },
  });

  const status = invoice ? invoiceDisplayStatus(invoice, todayYmd()) : null;
  const cfg = status ? COMPANY_INVOICE_STATUS_CONFIG[status] : null;

  return (
    <Dialog open onClose={onClose} className="sm:max-w-3xl">
      <DialogHeader onClose={onClose}>
        <AdminDialogHeading
          icon={Receipt}
          title={invoice?.invoiceNumber ?? "Nacrt fakture"}
          description={invoice ? `${invoice.companyName} · ${invoicePeriodLabel(invoice)}` : "Učitavanje..."}
        />
      </DialogHeader>
      <DialogBody>
        {invoice && cfg && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
              <span className={cn("inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-semibold", cfg.bg, cfg.color)}>
                <span className={cn("size-1.5 rounded-full", cfg.dot)} />
                {cfg.label}
              </span>
              <span>Za uplatu: <strong className="tabular-nums">{fmtKm(invoice.total)}</strong></span>
              <span className="text-muted-foreground">Osnovica {fmtKm(invoice.subtotal)}, PDV {fmtKm(invoice.vatAmount)}</span>
              {invoice.dueAt && <span className="text-muted-foreground">Rok: {dueLabel(invoice.dueAt)}</span>}
            </div>
            {invoice.status === "draft" && (
              <p className="text-xs text-muted-foreground">
                Nacrt prikazuje trenutno stanje. Stavke se zamrzavaju i faktura dobija broj tek kad je izdate.
              </p>
            )}
            {invoice.status === "void" && (
              <div className="space-y-0.5 text-xs text-muted-foreground">
                {invoice.voidedAt && (
                  <p>Datum storna: {formatDateWithOptionsBs(invoice.voidedAt, { day: "numeric", month: "short", year: "numeric", timeZone: "Europe/Sarajevo" })}</p>
                )}
                {invoice.voidReason && <p>Razlog storna: {invoice.voidReason}</p>}
                <p>PDF nosi žig STORNIRANO i nema podataka za uplatu.</p>
              </div>
            )}
            {invoice.kind === "credit_topup" && (
              <p className="text-xs text-muted-foreground">
                Faktura za dopunu kredita, plaćena pri dopuni. Seanse plaćene iz kredita nemaju poseban fiskalni račun.
              </p>
            )}

            {invoice.lines.length === 0 ? (
              <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                Nema seansi za fakturisanje u ovom periodu.
              </p>
            ) : (
              <div className="max-h-80 overflow-auto rounded-lg border border-border/60">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-muted/60 text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Datum</th>
                      <th className="px-3 py-2 text-left font-medium">Opis</th>
                      <th className="px-3 py-2 text-left font-medium">Zaposleni</th>
                      <th className="px-3 py-2 text-right font-medium">Iznos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.lines.map((l, i) => (
                      <tr key={l.id ?? `${l.sessionId}-${i}`} className="border-t border-border/40">
                        <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">{lineDate(l.scheduledAt)}</td>
                        <td className="px-3 py-2">{l.description}</td>
                        <td className="px-3 py-2 text-muted-foreground">{l.clientLabel}</td>
                        <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums" title={`Osnovica ${fmtKm(l.base)}, PDV ${fmtKm(l.vat)}`}>
                          {fmtKm(l.total)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {voiding && (
              <div className="space-y-2 rounded-lg border border-rose-200 bg-rose-50/50 p-3">
                <p className="text-sm font-medium">Storno fakture {invoice.invoiceNumber}</p>
                <p className="text-xs text-muted-foreground">Seanse se oslobađaju za novu fakturu; broj se ne koristi ponovo.</p>
                <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Razlog storna" />
              </div>
            )}
          </div>
        )}
      </DialogBody>
      <DialogFooter className="flex-wrap">
        {invoice?.status === "draft" && (
          <>
            <Button variant="ghost" disabled={action.isPending} onClick={() => action.mutate({ path: "void", body: { reason: "Nacrt obrisan" }, message: "Nacrt obrisan" })}>
              <Trash className="size-4" /> Obriši nacrt
            </Button>
            <Button disabled={action.isPending || invoice.lines.length === 0} onClick={() => action.mutate({ path: "issue", message: "Faktura izdata i poslana firmi" })}>
              <PaperPlaneTilt className="size-4" /> Izdaj i pošalji
            </Button>
          </>
        )}
        {invoice && invoice.status !== "draft" && (
          <Button variant="outline" asChild>
            <a href={`/api/company-invoices/${invoice.id}/pdf`} target="_blank" rel="noreferrer">
              <FilePdf className="size-4" /> PDF
            </a>
          </Button>
        )}
        {invoice?.status === "paid" && (
          <Button variant="outline" disabled={action.isPending} onClick={() => action.mutate({ path: "resend", message: "Faktura ponovo poslana" })}>
            <PaperPlaneTilt className="size-4" /> Pošalji ponovo
          </Button>
        )}
        {invoice?.status === "issued" && !voiding && (
          <>
            <Button variant="ghost" disabled={action.isPending} onClick={() => setVoiding(true)}>
              <Prohibit className="size-4" /> Storniraj
            </Button>
            <Button variant="outline" disabled={action.isPending} onClick={() => action.mutate({ path: "resend", message: "Faktura ponovo poslana" })}>
              <PaperPlaneTilt className="size-4" /> Pošalji ponovo
            </Button>
            <Button disabled={action.isPending} onClick={() => action.mutate({ path: "paid", message: "Faktura označena kao plaćena" })}>
              <CheckCircle className="size-4" /> Označi plaćenom
            </Button>
          </>
        )}
        {voiding && (
          <>
            <Button variant="outline" onClick={() => setVoiding(false)}>Odustani</Button>
            <Button
              variant="destructive"
              disabled={action.isPending || !reason.trim()}
              onClick={() => action.mutate({ path: "void", body: { reason: reason.trim() }, message: "Faktura stornirana" })}
            >
              Storniraj fakturu
            </Button>
          </>
        )}
      </DialogFooter>
    </Dialog>
  );
}
